import { useState, useEffect } from "react";
import axios from "axios";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Activity, Cpu, MemoryStick, Wifi, RefreshCw } from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { MONITORING } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs">
      <p className="text-slate-400 mb-1">{new Date(label).toLocaleTimeString()}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: <span className="font-mono font-medium">{p.value}{p.unit || ""}</span></p>
      ))}
    </div>
  );
};

function MetricChart({ title, data, keys, colors, unit = "%", testId, icon: Icon }) {
  return (
    <div data-testid={testId} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={14} className="text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{title}</h3>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={data} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
          <XAxis dataKey="timestamp" tick={{ fontSize: 9, fill: "#475569" }}
            tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            interval={Math.floor(data.length / 5)} />
          <YAxis tick={{ fontSize: 9, fill: "#475569" }} domain={[0, 100]} tickFormatter={(v) => `${v}${unit}`} />
          <Tooltip content={<CustomTooltip />} />
          {keys.map((key, i) => (
            <Area key={key} type="monotone" dataKey={key} name={key} stroke={colors[i]} strokeWidth={2} fill={colors[i]} fillOpacity={0.07} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function StatWidget({ label, value, unit, change, color }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">{label}</p>
      <p className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'IBM Plex Sans', sans-serif", color }}>
        {value}<span className="text-sm text-slate-500 ml-1 font-normal">{unit}</span>
      </p>
      {change !== undefined && (
        <p className={cn("text-xs mt-1", change > 0 ? "text-rose-400" : "text-emerald-400")}>
          {change > 0 ? "+" : ""}{change}% from 1h ago
        </p>
      )}
    </div>
  );
}

export default function Monitoring() {
  const { clusterId } = useCluster();
  const [history, setHistory] = useState([]);
  const [cluster, setCluster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetch = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/clusters/${clusterId}/metrics/history?hours=6`),
      axios.get(`${API}/clusters/${clusterId}`),
    ]).then(([h, c]) => {
      setHistory(h.data.slice(-48));
      setCluster(c.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, [clusterId]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, clusterId]);

  const latest = history[history.length - 1];

  return (
    <div data-testid={MONITORING.page} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Real-Time Monitoring</h2>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-3 h-3 accent-indigo-500" />
            Auto refresh (30s)
          </label>
          <button onClick={fetch} className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Current stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatWidget label="CPU Usage" value={latest?.cpu || cluster?.cpu_utilization || 0} unit="%" change={2.1}
          color={latest?.cpu > 80 ? "#f87171" : "#34d399"} />
        <StatWidget label="Memory Usage" value={latest?.memory || cluster?.memory_utilization || 0} unit="%" change={0.5}
          color={latest?.memory > 85 ? "#f87171" : "#22d3ee"} />
        <StatWidget label="Network In" value={latest?.network_in || 0} unit="MB/s" change={-1.2} color="#a78bfa" />
        <StatWidget label="Network Out" value={latest?.network_out || 0} unit="MB/s" change={0.8} color="#fbbf24" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MetricChart
          data-testid={MONITORING.cpuChart}
          title="CPU Utilization" data={history}
          keys={["cpu"]} colors={["#6366f1"]}
          icon={Cpu} unit="%" testId={MONITORING.cpuChart}
        />
        <MetricChart
          title="Memory Utilization" data={history}
          keys={["memory"]} colors={["#22d3ee"]}
          icon={MemoryStick} unit="%" testId={MONITORING.memoryChart}
        />
      </div>

      {/* Network chart full width */}
      <div data-testid={MONITORING.networkChart} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
        <div className="flex items-center gap-2 mb-4">
          <Wifi size={14} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Network Traffic</h3>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={history} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="timestamp" tick={{ fontSize: 9, fill: "#475569" }}
              tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              interval={Math.floor(history.length / 5)} />
            <YAxis tick={{ fontSize: 9, fill: "#475569" }} tickFormatter={(v) => `${v}MB`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
            <Line type="monotone" dataKey="network_in" name="In" stroke="#a78bfa" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="network_out" name="Out" stroke="#fbbf24" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Pod health table */}
      {cluster && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-md overflow-hidden animate-fade-in">
          <div className="px-4 py-3 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Pod Health Summary</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-800">
            {[
              { label: "Running", value: cluster.pods_running, color: "text-emerald-400" },
              { label: "Pending", value: cluster.pods_pending, color: "text-amber-400" },
              { label: "Failed", value: cluster.pods_failed, color: "text-rose-400" },
              { label: "Total", value: cluster.pods_total, color: "text-slate-200" },
            ].map(({ label, value, color }) => (
              <div key={label} className="px-6 py-4 text-center">
                <p className={cn("text-2xl font-semibold font-mono", color)}>{value}</p>
                <p className="text-xs text-slate-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
